import type { components } from '../../api-types';
import {
  type NodeManifest,
  type ScopeNodeChain,
  type ScopeNodeCountry,
  ScopeNodeDecision,
  type ScopeNodeInstallation,
  type ScopeNodeLocation,
  type ScopeNodeRegion,
  type ScopeNodeSubChain,
  ScopeNodeType
} from '$lib/types/installationScopeTree';

function mapManifests(
  manifests: components['schemas']['NodeManifestRef'][] | undefined
): NodeManifest[] {
  return (manifests ?? []).map((m) => ({ id: m.id, name: m.name }));
}

function mapInstallationNode(
  apiNode: components['schemas']['InstallationNode']
): ScopeNodeInstallation {
  return {
    id: apiNode.id,
    name: apiNode.name || apiNode.id,
    orphaned: apiNode.orphaned ?? false,
    decision: apiNode.decision as ScopeNodeDecision,
    scopeNodeType: ScopeNodeType.Installation,
    children: [],
    installationCount: apiNode.installationCount ?? 0,
    manifests: mapManifests(apiNode.manifests)
  };
}

function mapLocationNode(apiNode: components['schemas']['LocationNode']): ScopeNodeLocation {
  return {
    id: apiNode.id,
    name: apiNode.name || apiNode.id,
    orphaned: apiNode.orphaned ?? false,
    decision: apiNode.decision as ScopeNodeDecision,
    scopeNodeType: ScopeNodeType.Location,
    children: apiNode.installations?.map(mapInstallationNode),
    installationCount: apiNode.installationCount ?? 0,
    manifests: mapManifests(apiNode.manifests)
  };
}

function mapRegionNode(apiNode: components['schemas']['RegionNode']): ScopeNodeRegion {
  return {
    id: apiNode.id,
    name: apiNode.name || apiNode.id,
    orphaned: apiNode.orphaned ?? false,
    decision: apiNode.decision as ScopeNodeDecision,
    scopeNodeType: ScopeNodeType.Region,
    children: apiNode.locations?.map(mapLocationNode),
    installationCount: apiNode.installationCount ?? 0,
    manifests: mapManifests(apiNode.manifests)
  };
}

function mapSubChainNode(apiNode: components['schemas']['SubChainNode']): ScopeNodeSubChain {
  return {
    id: apiNode.id,
    name: apiNode.name || apiNode.id,
    orphaned: apiNode.orphaned ?? false,
    decision: apiNode.decision as ScopeNodeDecision,
    scopeNodeType: ScopeNodeType.SubChain,
    children: apiNode.regions?.map(mapRegionNode),
    installationCount: apiNode.installationCount ?? 0,
    manifests: mapManifests(apiNode.manifests)
  };
}

function mapChainNode(apiNode: components['schemas']['ChainNode']): ScopeNodeChain {
  return {
    id: apiNode.id,
    name: apiNode.name || apiNode.id,
    orphaned: apiNode.orphaned ?? false,
    decision: apiNode.decision as ScopeNodeDecision,
    scopeNodeType: ScopeNodeType.Chain,
    children: apiNode.subChains?.map(mapSubChainNode),
    installationCount: apiNode.installationCount ?? 0,
    manifests: mapManifests(apiNode.manifests)
  };
}

export function mapApiScopeToDomainScope(
  apiScope: components['schemas']['InstallationScopeTree']
): ScopeNodeCountry {
  return {
    id: apiScope.id,
    name: apiScope.name || apiScope.id,
    orphaned: apiScope.orphaned ?? false,
    decision: apiScope.decision as ScopeNodeDecision,
    scopeNodeType: ScopeNodeType.Country,
    children: apiScope.chains?.map(mapChainNode),
    installationCount: apiScope.installationCount ?? 0,
    manifests: mapManifests(apiScope.manifests)
  };
}

// --- Convert domain scope tree to API format with pruning ---

/**
 * Converts an Installation node to API format.
 * Returns null if the node should be pruned (PASSTHROUGH decision on a leaf node).
 */
function convertInstallationNodeToApi(
  node: ScopeNodeInstallation
): components['schemas']['InstallationNode'] | null {
  // Installation is a leaf node - prune if PASSTHROUGH
  if (node.decision === ScopeNodeDecision.Passthrough) {
    return null;
  }
  return {
    id: node.id,
    orphaned: node.orphaned,
    decision: node.decision as components['schemas']['Decision'],
    type: 'INSTALLATION_ID'
  };
}

/**
 * Converts a Location node to API format.
 * Returns null if the node should be pruned (PASSTHROUGH with all children pruned).
 */
function convertLocationNodeToApi(
  node: ScopeNodeLocation
): components['schemas']['LocationNode'] | null {
  const installations = (node.children ?? [])
    .map(convertInstallationNodeToApi)
    .filter((n): n is components['schemas']['InstallationNode'] => n !== null);

  // Prune if PASSTHROUGH and no remaining children
  if (node.decision === ScopeNodeDecision.Passthrough && installations.length === 0) {
    return null;
  }

  return {
    id: node.id,
    orphaned: node.orphaned,
    decision: node.decision as components['schemas']['Decision'],
    type: 'LOCATION_ID',
    installations
  };
}

/**
 * Converts a Region node to API format.
 * Returns null if the node should be pruned (PASSTHROUGH with all children pruned).
 */
function convertRegionNodeToApi(node: ScopeNodeRegion): components['schemas']['RegionNode'] | null {
  const locations = (node.children ?? [])
    .map(convertLocationNodeToApi)
    .filter((n): n is components['schemas']['LocationNode'] => n !== null);

  // Prune if PASSTHROUGH and no remaining children
  if (node.decision === ScopeNodeDecision.Passthrough && locations.length === 0) {
    return null;
  }

  return {
    id: node.id,
    orphaned: node.orphaned,
    decision: node.decision as components['schemas']['Decision'],
    type: 'REGION_ID',
    locations
  };
}

/**
 * Converts a SubChain node to API format.
 * Returns null if the node should be pruned (PASSTHROUGH with all children pruned).
 */
function convertSubChainNodeToApi(
  node: ScopeNodeSubChain
): components['schemas']['SubChainNode'] | null {
  const regions = (node.children ?? [])
    .map(convertRegionNodeToApi)
    .filter((n): n is components['schemas']['RegionNode'] => n !== null);

  // Prune if PASSTHROUGH and no remaining children
  if (node.decision === ScopeNodeDecision.Passthrough && regions.length === 0) {
    return null;
  }

  return {
    id: node.id,
    orphaned: node.orphaned,
    decision: node.decision as components['schemas']['Decision'],
    type: 'SUB_CHAIN_ID',
    regions
  };
}

/**
 * Converts a Chain node to API format.
 * Returns null if the node should be pruned (PASSTHROUGH with all children pruned).
 */
function convertChainNodeToApi(node: ScopeNodeChain): components['schemas']['ChainNode'] | null {
  const subChains = (node.children ?? [])
    .map(convertSubChainNodeToApi)
    .filter((n): n is components['schemas']['SubChainNode'] => n !== null);

  // Prune if PASSTHROUGH and no remaining children
  if (node.decision === ScopeNodeDecision.Passthrough && subChains.length === 0) {
    return null;
  }

  return {
    id: node.id,
    orphaned: node.orphaned,
    decision: node.decision as components['schemas']['Decision'],
    type: 'CHAIN_ID',
    subChains
  };
}

/**
 * Converts the domain scope tree (ScopeNodeCountry) to API format.
 * Prunes PASSTHROUGH nodes that have no children with explicit decisions.
 * The Country node is always present (never pruned).
 */
export function convertScopeTreeToApiFormat(
  scopeTree: ScopeNodeCountry
): components['schemas']['InstallationScopeTree'] {
  const chains = (scopeTree.children ?? [])
    .map(convertChainNodeToApi)
    .filter((n): n is components['schemas']['ChainNode'] => n !== null);

  return {
    id: scopeTree.id,
    orphaned: scopeTree.orphaned,
    decision: scopeTree.decision as components['schemas']['Decision'],
    type: 'COUNTRY',
    chains
  };
}
